/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { reviewSessionService } from '../../services/reviewSession.service';
import { ChevronLeft, BookOpen } from 'lucide-react';

const fadeIn = keyframes`from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); }`;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, rgba(0,150,255,0.35) 0%, rgba(100,200,255,0.25) 100%);
  padding: 0;
`;

const PageContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  animation: ${fadeIn} 0.35s ease;
`;

const BackBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  background: rgba(255,255,255,0.85); border: none;
  border-radius: 20px; padding: 8px 16px;
  font-size: 13px; font-weight: 600; color: #1a1a2e;
  cursor: pointer; margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  transition: all 0.2s;
  &:hover { background: #fff; transform: translateX(-2px); }
`;

const Card = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 24px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
`;

const PageTitle = styled.h1`
  font-size: 22px; font-weight: 700; color: #1a1a2e;
  margin: 0 0 4px; display: flex; align-items: center; gap: 10px;
`;

const Subtitle = styled.p`
  font-size: 13px; color: #888; margin: 0 0 24px;
`;

const ReviewList = styled.div`
  display: flex; flex-direction: column; gap: 12px;
`;

const ReviewItem = styled.div`
  border: 1.5px solid #eaeef6;
  border-radius: 12px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #fafbff;
  cursor: pointer;
  transition: all 0.18s;
  &:hover {
    border-color: #a5b4fc;
    background: #f0f4ff;
    box-shadow: 0 4px 14px rgba(102,126,234,0.12);
    transform: translateX(4px);
  }
`;

const ItemInfo = styled.div`
  flex: 1;
`;

const ItemName = styled.div`
  font-size: 15px; font-weight: 600; color: #1a1a2e; margin-bottom: 4px;
`;

const ItemMeta = styled.div`
  font-size: 12px; color: #888;
`;

const ItemIcon = styled.div`
  width: 36px; height: 36px;
  border-radius: 10px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex; align-items: center; justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #aaa;
  font-size: 15px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #888;
`;

const formatDate = (dateStr: string) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const StudentReviewListPage: React.FC = () => {
  const navigate = useNavigate();
  const currentUser = useSelector((state: any) => state.authentication?.userInfo);

  const [sessions, setSessions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await reviewSessionService.getMyReviewSessions();
        setSessions(res.data || []);
      } catch (err: any) {
        setError('Không thể tải danh sách bài ôn tập.');
      } finally {
        setLoading(false);
      }
    };
    if (currentUser?.id) load();
  }, [currentUser]);

  return (
    <PageContainer>
      <PageContent>
        <BackBtn onClick={() => navigate('/student')}>
          <ChevronLeft size={15} /> Quay lại
        </BackBtn>

        <Card>
          <PageTitle>
            <BookOpen size={22} color="#667eea" />
            Ôn tập bài cũ
          </PageTitle>
          <Subtitle>
            Danh sách các bài ôn tập từ vựng và mẫu câu của lớp bạn
          </Subtitle>

          {loading && <LoadingState>⏳ Đang tải...</LoadingState>}
          {error && <EmptyState>❌ {error}</EmptyState>}

          {!loading && !error && sessions.length === 0 && (
            <EmptyState>
              📭 Chưa có bài ôn tập nào. Giáo viên sẽ tạo bài ôn sau mỗi buổi học.
            </EmptyState>
          )}

          {!loading && sessions.length > 0 && (
            <ReviewList>
              {sessions.map((session) => (
                <ReviewItem
                  key={session.id}
                  onClick={() => navigate(`/review/${session.id}`)}
                >
                  <ItemIcon><BookOpen size={16} /></ItemIcon>
                  <ItemInfo>
                    <ItemName>{session.name}</ItemName>
                    <ItemMeta>📅 {formatDate(session.lesson_date)}</ItemMeta>
                  </ItemInfo>
                  <ChevronLeft size={16} style={{ transform: 'rotate(180deg)', color: '#aaa' }} />
                </ReviewItem>
              ))}
            </ReviewList>
          )}
        </Card>
      </PageContent>
    </PageContainer>
  );
};

export default StudentReviewListPage;
